// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import {
  SectionHeader,
  EditableSelectComponent,
  EditableTextareaComponent,
  EditableInputComponent,
} from "components";
import { Document } from "assets/icons"; // Replace with appropriate icons
import { useSession } from "@clerk/clerk-react";

// Assuming BuildingStabilityInfoDTO is defined elsewhere in your application
import {
  BuildingInspectionInfo,
  DoorbellType,
  GutterStatus,
  ProtectionUnderRoofStatus,
  RainWaterPipesStatus,
  RoofStatus,
  StairStatus,
} from "shared/types/building-inspection";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { FileLabel } from "shared/types/file";

interface BuildingVisualInspectionInfoComponentProps {
  inspectionInfo: BuildingInspectionInfo;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const BuildingVisualInspectionInfoComponent = ({
  inspectionInfo,
  handleCloseParent,
  handleInspectionInfoChangeInParent,
  building,
  parentEditMode = true,
}: BuildingVisualInspectionInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedInspectionInfo, setEditedInspectionInfo] =
    useState(inspectionInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();

  useEffect(() => {
    setEditedInspectionInfo(inspectionInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      await fetchWithAuth(
        `${API_URL}/building/building-inspection-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedInspectionInfo),
        },
        session,
      );
      toast.success("Visuele inspectie informatie succesvol bijgewerkt!");
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de visuele inspectie.",
      );
    }
  };

  const updateInspectionInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedInspectionInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...inspectionInfo,
        ...updatedFields,
      });
    }
  };

  const getStatusColorForStairWidth = (width: any) => {
    if (!width) {
      return "bg-transparent-500";
    }

    if (Number(width) >= 80) {
      return "bg-green-500";
    } else if (Number(width) >= 70 && Number(width) < 80) {
      return "bg-orange-500";
    } else if (Number(width) < 70) {
      return "bg-red-500";
    } else {
      return "bg-transparent-500";
    }
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case RainWaterPipesStatus.OK:
      case ProtectionUnderRoofStatus.OK:
      case ProtectionUnderRoofStatus.NOT_REQUIRED:
      case RoofStatus.OK:
      case StairStatus.OK:
      case StairStatus.NOT_REQUIRED:
      case GutterStatus.OK:
      case DoorbellType.CLASSIC:
      case DoorbellType.VIDEO:
        return "bg-green-500";
      case RainWaterPipesStatus.NOT_OK:
      case ProtectionUnderRoofStatus.NOT_OK:
      case ProtectionUnderRoofStatus.NOT_OK:
      case RoofStatus.NOT_OK:
      case StairStatus.NOT_OK:
      case StairStatus.NOT_OK:
      case GutterStatus.NOT_OK:
      case DoorbellType.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  const onUpdateRoofStatusDescription = (remarks: string) => {
    updateInspectionInfo({ roofStatusDescription: remarks });
  };

  const onUpdateDoorbellModel = (value: string) => {
    updateInspectionInfo({ doorbellModel: value });
  };

  const onUpdateDoorbellBrand = (value: string) => {
    updateInspectionInfo({ doorbellBrand: value });
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Visuele inspectie Informatie"
        icon={<Document />}
        editMode={editMode}
        sticky={false}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's voorgevel</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              fileLabel={FileLabel.FrontagePicture}
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's achtergevel</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              fileLabel={FileLabel.BackFacadePicture}
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.rainWaterPipesStatus,
                )}`}
              />

              <p>Staat van de regenafvoeren?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(RainWaterPipesStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.rainWaterPipesStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ rainWaterPipesStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's regenwaterafvoeren</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              fileLabel={FileLabel.PictureRainwaterPipe}
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.gutterStatus,
                )}`}
              />

              <p>Staat van de dakgoot?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(GutterStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.gutterStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ gutterStatus: value })
              }
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's dakgoot</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              fileLabel={FileLabel.PictureGutter}
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.doorbellType,
                )}`}
              />

              <p>Deurbel type</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(DoorbellType).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.doorbellType}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ doorbellType: value })
              }
            />
          </div>
          {(editedInspectionInfo?.doorbellType == DoorbellType.CLASSIC ||
            editedInspectionInfo?.doorbellType == DoorbellType.VIDEO) && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Foto's deurbel</p>
              </header>
              <ImagesContainer
                buildingId={building?.id as string}
                fileLabel={FileLabel.PictureDoorbell}
              />
            </div>
          )}

          {(editedInspectionInfo?.doorbellType == DoorbellType.CLASSIC ||
            editedInspectionInfo?.doorbellType == DoorbellType.VIDEO) && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Merk van de deurbel</p>
              </header>
              <EditableTextareaComponent
                label="Merk van de deurbel"
                value={editedInspectionInfo?.doorbellBrand || ""}
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateDoorbellBrand}
              />
            </div>
          )}

          {(editedInspectionInfo?.doorbellType == DoorbellType.CLASSIC ||
            editedInspectionInfo?.doorbellType == DoorbellType.VIDEO) && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Model van de deurbel</p>
              </header>
              <EditableTextareaComponent
                label="Model van de deurbel"
                value={editedInspectionInfo?.doorbellModel || ""}
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateDoorbellModel}
              />
            </div>
          )}

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.protectionUnderRoofStatus,
                )}`}
              />

              <p>Staat onderdak / Waterkering dak</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(ProtectionUnderRoofStatus).map(
                (value) => ({ label: value, value: value }),
              )}
              value={editedInspectionInfo?.protectionUnderRoofStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ protectionUnderRoofStatus: value })
              }
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.roofStatus,
                )}`}
              />

              <p>Algemene staat dak</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(RoofStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.roofStatus}
              editMode={editMode}
              onChange={(value) => updateInspectionInfo({ roofStatus: value })}
            />
          </div>

          {editedInspectionInfo?.roofStatus == RoofStatus.NOT_OK && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Beschrijving van de gebreken aan het dak</p>
              </header>
              <EditableTextareaComponent
                label="Geef opmerkingen of toelichting hier"
                value={editedInspectionInfo?.roofStatusDescription || ""}
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateRoofStatusDescription}
              />
            </div>
          )}

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's hoofddak</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              fileLabel={FileLabel.MainRoofPicture}
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's bijdak</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              fileLabel={FileLabel.SideRoofPicture}
            />
          </div>

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.stairStatus,
                )}`}
              />

              <p>Algemene staat trappen in de gemeenschap</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(StairStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.stairStatus}
              editMode={editMode}
              onChange={(value) => updateInspectionInfo({ stairStatus: value })}
            />
          </div>

          {editedInspectionInfo?.stairStatus != StairStatus.NOT_REQUIRED && (
            <>
              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColorForStairWidth(
                      editedInspectionInfo?.stairWidth,
                    )}`}
                  />

                  <p>
                    Wat is de breedte van de trap in de gemeenschap? (in cm)
                  </p>
                </header>
                <EditableInputComponent
                  label="Vul de breedte in in centimeters"
                  value={editedInspectionInfo?.stairWidth}
                  editMode={editMode}
                  isLoading={false}
                  onChange={(value) =>
                    updateInspectionInfo({ stairWidth: value })
                  }
                />
              </div>

              <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                <header className="flex items-center gap-2 text-sm font-medium">
                  <p>Foto's van de trappen in de trappenhal</p>
                </header>
                <ImagesContainer
                  buildingId={building?.id as string}
                  fileLabel={FileLabel.PictureStairsCommonHall}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default BuildingVisualInspectionInfoComponent;
